import React, { useState, useEffect, useCallback } from 'react';
import './SunscreenManagement.css';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// Import icons
import { FiPlus, FiEdit3, FiTrash2, FiSun, FiDroplet, FiPackage, FiSearch, FiFilter, FiCheckCircle, FiXCircle, FiX, FiUsers, FiRefreshCw, FiTarget } from 'react-icons/fi';

const SunscreenManagement = () => {
  const [sunscreenTypes, setSunscreenTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [newType, setNewType] = useState({
    name: '',
    sizes: { 'SMALL': 100, 'MEDIUM': 200, 'LARGE': 300 }
  });

  // Refill functionality state
  const [showRefillSection, setShowRefillSection] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [selectedSKU, setSelectedSKU] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [refillLoading, setRefillLoading] = useState(false);
  const [deviceSlots, setDeviceSlots] = useState([]);
  
  // Mock device data - replace with real data from your blocks context/API
  const [availableDevices] = useState([
    { serial: 'BLK001', name: 'Beach Location A', slots: [{ id: 0, fillLevel: 25, type: 'SPF 30' }, { id: 1, fillLevel: 80, type: 'SPF 50' }] },
    { serial: 'BLK002', name: 'Pool Area B', slots: [{ id: 0, fillLevel: 5, type: 'Empty' }, { id: 1, fillLevel: 60, type: 'Kids SPF 30' }] }
  ]);

  const { user } = useAuth();

  const fetchSunscreenTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.config.getSunscreenConfiguration();
      setSunscreenTypes(response.data || {});
      setError(null);
    } catch (err) {
      setError("Error fetching sunscreen types: " + err.message);
      setSunscreenTypes({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSunscreenTypes();
  }, [fetchSunscreenTypes]);

  // Helper functions
  const getSPFColor = (typeName) => {
    const spfMatch = typeName.match(/\d+/);
    const spf = spfMatch ? parseInt(spfMatch[0]) : 30;
    
    if (spf >= 70) return '#7B68EE'; // Purple
    if (spf >= 50) return '#4A90E2'; // Blue
    if (spf >= 30) return '#FF6B35'; // Orange
    return '#FFA500'; // Yellow
  };

  // Filter sunscreen types based on search
  const filteredTypes = Object.entries(sunscreenTypes).filter(([typeName, sizes]) => {
    const matchesSearch = searchTerm === '' || 
                         typeName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || 
                         (selectedFilter === 'spf30' && typeName.toLowerCase().includes('30')) ||
                         (selectedFilter === 'spf50' && typeName.toLowerCase().includes('50')) ||
                         (selectedFilter === 'kids' && typeName.toLowerCase().includes('kids'));
    
    return matchesSearch && matchesFilter;
  });

  // Handle add/edit functionality
  const handleAddType = () => {
    setEditingType(null);
    setShowModal(true);
  };

  const handleEditType = (typeName, sizes) => {
    setEditingType({ name: typeName, sizes });
    setNewType({ name: typeName, sizes });
    setShowModal(true);
  };

  const handleSaveType = async () => {
    try {
      if (!newType.name || !newType.sizes) {
        setError('Please fill in all required fields.');
        return;
      }

      const updatedTypes = { ...sunscreenTypes };
      
      if (editingType && editingType.name !== newType.name) {
        // Rename: delete old and add new
        delete updatedTypes[editingType.name];
      }
      
      updatedTypes[newType.name] = newType.sizes;
      
      // Call API to update configuration
      await api.config.updateSunscreenConfiguration(updatedTypes);
      
      setSunscreenTypes(updatedTypes);
      setShowModal(false);
      setSuccess('Sunscreen type saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      setError('Error saving sunscreen type: ' + err.message);
    }
  };

  const handleDeleteType = (typeName) => {
    setDeleteConfirmation(typeName);
  };

  const confirmDeleteType = async () => {
    try {
      const updatedTypes = { ...sunscreenTypes };
      delete updatedTypes[deleteConfirmation];
      
      // Call API to update configuration
      await api.config.updateSunscreenConfiguration(updatedTypes);
      
      setSunscreenTypes(updatedTypes);
      setDeleteConfirmation(null);
      setSuccess('Sunscreen type deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      setError('Error deleting sunscreen type: ' + err.message);
    }
  };

  // Refill functionality
  const handleDeviceSelect = (deviceSerial) => {
    setSelectedDevice(deviceSerial);
    const device = availableDevices.find(d => d.serial === deviceSerial);
    setDeviceSlots(device ? device.slots : []);
    setSelectedSlot(null);
    setSelectedSKU(null);
    setSelectedSize(null);
  };

  const handleSKUSelect = (typeName, sizes) => {
    setSelectedSKU({ name: typeName, sizes });
    setSelectedSize(null);
  };

  const handleRefill = async () => {
    if (!selectedDevice || selectedSlot === null || !selectedSKU || !selectedSize) {
      setError('Please select device, slot, SKU, and size');
      return;
    }

    try {
      setRefillLoading(true);
      
      // Call the refill API with the updated parameters
      await api.device.registerRefill(selectedDevice, {
        volumeML: selectedSize.volume,
        sunscreenType: selectedSKU.name,
        slotIndex: selectedSlot
      });
      
      setSuccess(`Successfully refilled Slot ${selectedSlot + 1} with ${selectedSKU.name} (${selectedSize.volume}ml)`);
      setTimeout(() => setSuccess(null), 5000);
      
      // Reset selections
      setSelectedSlot(null);
      setSelectedSKU(null);
      setSelectedSize(null);
      
    } catch (err) {
      setError('Refill failed: ' + err.message);
    } finally {
      setRefillLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="sunscreen-management">
      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="page-title">
              <FiSun className="page-icon" />
              Sunscreen Product Library
            </h1>
            <p className="page-subtitle">Manage your sunscreen types and configurations</p>
          </div>
          <div className="header-right">
            <button 
              className={`btn-toggle-refill ${showRefillSection ? 'active' : ''}`}
              onClick={() => setShowRefillSection(!showRefillSection)}
            >
              <FiRefreshCw /> {showRefillSection ? 'Hide' : 'Show'} Refill
            </button>
            <button 
              className="btn-add-new"
              onClick={handleAddType}
            >
              <FiPlus /> Add New
            </button>
          </div>
        </div>
      </div>

      {/* Refill Section */}
      {showRefillSection && (
        <div className="refill-section">
          <div className="refill-header">
            <h3 className="refill-title">
              <FiTarget className="refill-icon" />
              Quick Refill
            </h3>
            <p className="refill-subtitle">Select device, slot, and sunscreen to refill</p>
          </div>
          
          <div className="refill-grid">
            {/* Device Selection */}
            <div className="refill-step">
              <label className="step-label">1. Select Device</label>
              <select 
                className="refill-select"
                value={selectedDevice}
                onChange={(e) => handleDeviceSelect(e.target.value)}
              >
                <option value="">Choose device...</option>
                {availableDevices.map(device => (
                  <option key={device.serial} value={device.serial}>
                    {device.name} ({device.serial})
                  </option>
                ))}
              </select>
            </div>

            {/* Slot Selection */}
            {selectedDevice && (
              <div className="refill-step">
                <label className="step-label">2. Select Slot</label>
                <div className="slot-grid">
                  {deviceSlots.map((slot, index) => (
                    <div 
                      key={index}
                      className={`slot-card ${selectedSlot === index ? 'selected' : ''} ${slot.fillLevel < 20 ? 'low' : ''}`}
                      onClick={() => setSelectedSlot(index)}
                    >
                      <div className="slot-number">Slot {index + 1}</div>
                      <div className="slot-fill">{slot.fillLevel}%</div>
                      <div className="slot-type">{slot.type}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SKU Selection */}
            {selectedSlot !== null && (
              <div className="refill-step">
                <label className="step-label">3. Select Sunscreen</label>
                <div className="sku-grid">
                  {Object.entries(sunscreenTypes).slice(0, 4).map(([typeName, sizes]) => (
                    <div 
                      key={typeName}
                      className={`sku-card ${selectedSKU?.name === typeName ? 'selected' : ''}`}
                      onClick={() => handleSKUSelect(typeName, sizes)}
                    >
                      <div className="sku-name">{typeName}</div>
                      <div className="sku-spf">SPF {typeName.match(/\d+/)?.[0] || '30'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Size Selection */}
            {selectedSKU && (
              <div className="refill-step">
                <label className="step-label">4. Select Size</label>
                <div className="size-grid">
                  {Object.entries(selectedSKU.sizes).map(([sizeName, volume]) => (
                    <div 
                      key={sizeName}
                      className={`size-card ${selectedSize?.name === sizeName ? 'selected' : ''}`}
                      onClick={() => setSelectedSize({ name: sizeName, volume })}
                    >
                      <div className="size-name">{sizeName}</div>
                      <div className="size-volume">{volume}ml</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Refill Action */}
          {selectedDevice && selectedSlot !== null && selectedSKU && selectedSize && (
            <div className="refill-action">
              <button 
                className="btn-refill"
                onClick={handleRefill}
                disabled={refillLoading}
              >
                {refillLoading ? (
                  <>
                    <div className="spinner"></div>
                    Refilling...
                  </>
                ) : (
                  <>
                    <FiRefreshCw />
                    Refill Slot {selectedSlot + 1} with {selectedSKU.name} ({selectedSize.volume}ml)
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div className="error-banner" onClick={() => setError(null)}>{error}</div>}

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-left">
          <div className="search-input">
            <FiSearch className="search-icon" />
            <input 
              type="text"
              placeholder="Search sunscreens by brand, SPF, or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="filter-right">
          <select 
            value={selectedFilter}
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="spf30">SPF 30</option>
            <option value="spf50">SPF 50</option>
            <option value="kids">Kids Formula</option>
          </select>
        </div>
      </div>

      {/* Sunscreen Cards Grid */}
      <div className="sunscreen-cards-grid">
        {filteredTypes.map(([typeName, sizes]) => (
          <div key={typeName} className="sunscreen-card">
            <div className="card-header">
              <div className="brand-info">
                <div 
                  className="spf-circle"
                  style={{ backgroundColor: getSPFColor(typeName) }}
                >
                  <FiSun className="spf-icon" />
                </div>
                <div className="type-info">
                  <h3 className="type-name">{typeName}</h3>
                  <p className="type-sizes">{Object.keys(sizes).length} sizes available</p>
                </div>
              </div>
              <div className="card-actions">
                <button 
                  className="card-action-btn"
                  onClick={() => handleEditType(typeName, sizes)}
                  title="Edit sunscreen type"
                >
                  <FiEdit3 />
                </button>
                <button 
                  className="card-action-btn delete"
                  onClick={() => handleDeleteType(typeName)}
                  title="Delete sunscreen type"
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
            
            <div className="card-content">
              <div className="sizes-grid">
                {Object.entries(sizes).map(([sizeName, volumeMl]) => (
                  <div key={sizeName} className="size-item">
                    <FiDroplet className="size-icon" />
                    <span className="size-name">{sizeName}</span>
                    <span className="size-volume">{volumeMl}ml</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {/* Success Message */}
      {success && (
        <div className="success-banner">
          <FiCheckCircle className="success-icon" />
          {success}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <div className="modal-icon">
                  <FiPackage />
                </div>
                <div>
                  <h2>{editingType ? 'Edit Sunscreen Type' : 'Add New Sunscreen'}</h2>
                  <p>Configure sunscreen type and sizes</p>
                </div>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Type Name *</label>
                <input 
                  type="text"
                  value={newType.name}
                  onChange={(e) => setNewType({...newType, name: e.target.value})}
                  placeholder="e.g., SPF 30, Kids SPF 50"
                />
              </div>
              
              <div className="form-group">
                <label>Available Sizes (ml) *</label>
                <div className="sizes-input">
                  {Object.entries(newType.sizes).map(([sizeName, volumeMl]) => (
                    <div key={sizeName} className="size-input-row">
                      <input 
                        type="text"
                        value={sizeName}
                        onChange={(e) => {
                          const newSizes = { ...newType.sizes };
                          delete newSizes[sizeName];
                          newSizes[e.target.value] = volumeMl;
                          setNewType({...newType, sizes: newSizes});
                        }}
                        placeholder="Size name"
                      />
                      <input 
                        type="number"
                        value={volumeMl}
                        onChange={(e) => {
                          const newSizes = { ...newType.sizes };
                          newSizes[sizeName] = parseInt(e.target.value) || 0;
                          setNewType({...newType, sizes: newSizes});
                        }}
                        placeholder="Volume (ml)"
                        min="1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-save"
                onClick={handleSaveType}
                disabled={!newType.name || Object.keys(newType.sizes).length === 0}
              >
                {editingType ? 'Update' : 'Create'} Type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        onConfirm={confirmDeleteType}
        title="Delete Sunscreen Type"
        message={`Are you sure you want to delete "${deleteConfirmation}"? This action cannot be undone.`}
        actionType="delete"
        itemName={deleteConfirmation}
      />
    </div>
  );
};

export default SunscreenManagement;